import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import { DataTableHeadCellData, DataTableHeader } from './DataTableHeader';
import { DataTableToolbar } from './DataTableToolBar';
import { CircularProgress } from '@mui/material';

function extractText(element: any): string {
  if (!element) return '';

  if (typeof element === 'string') {
    return element;
  }

  if (element && React.isValidElement(element)) {
    // @ts-ignore
    const childs: any = React.Children.map(element.props.children, extractText);
    if (!childs) return '';
    else return childs.join(' ');
  }

  return '';
}

function doSearchRow(row: any, searchKeys: string[], searchQuery: string) {
  if (searchKeys.length == 0) return true;

  let result = false;
  for (let j=0; j<searchKeys.length; j++) {
    const val: string = extractText(row?.[searchKeys[j]] ?? '');
    if (val.toLowerCase().includes(searchQuery.toLowerCase())) {
      return true;
    }
  }

  return false;
}

interface CustomTreeTableRowProps {
  isItemSelected?: boolean;
  row: any;
  depth?: number;
  handleClick: (e: any, id: number) => void;
  headCells: DataTableHeadCellData[];
  selected: number[];
  searchQuery: string;
  hideIdCheckbox?: boolean;
}

const CustomTreeTableRow = ({ row, isItemSelected, headCells, selected, hideIdCheckbox, searchQuery, handleClick, depth = 0 }: CustomTreeTableRowProps) => {
  const labelId = `data-tree-checkbox-${row?.id}`;
  const searchAbleKeys = headCells.filter((item) => item.searchable).map((item) => item.id);

  return (
    <>
    {
      doSearchRow(row, searchAbleKeys, searchQuery) ? (
        <TableRow
          hover
          role="checkbox"
          aria-checked={isItemSelected}
          tabIndex={-1}
          key={row.id}
          selected={isItemSelected}
        >
          {
            !hideIdCheckbox && (
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  checked={isItemSelected}
                  onClick={(event) => handleClick(event, row?.id)}
                  inputProps={{
                    'aria-labelledby': labelId,
                  }}
                />
              </TableCell>
            )
          }
          {
            headCells.map((key: any, index: number) => {
              return index === 0 ? (
                <TableCell
                  component="th"
                  id={labelId}
                  scope="row"
                  padding="none"
                  key={index}
                  style={{ paddingLeft: hideIdCheckbox ? `calc(20px + ${depth * 2}rem)` : `${depth * 2}rem` }}
                >
                  {row?.[key.id]}
                </TableCell>
              ) : (
                <TableCell 
                  key={index} 
                  sx={{ paddingLeft: '0px', paddingRight: '0px' }} 
                  align={ key?.cellAlign ?? 'center' }
                >
                  {row?.[key.id]}
                </TableCell>
              )
            })
          }
        </TableRow>
      ) : null
    }
      {(row?.children??[]).map((child: any) => (
        <CustomTreeTableRow 
          key={child.id} 
          row={child} 
          isItemSelected={selected.indexOf(child?.id) !== -1} 
          selected={selected}
          headCells={headCells} 
          handleClick={handleClick} 
          searchQuery={searchQuery}
          depth={depth + 1}
          hideIdCheckbox={hideIdCheckbox}
        />
      ))}
    </>
  );
};

interface DataTreeProps {
  headCells: DataTableHeadCellData[],
  rows: any[],
  title?: string;
  hideToolBar?: boolean;
  onBulkDelete?: (ids: number[]) => void;
  isLoading?: boolean;
  hideIdCheckbox?: boolean;
  hideDeleteButton?: boolean;
}

export const DataTree = ({ headCells, rows, title, hideToolBar, hideIdCheckbox, hideDeleteButton, onBulkDelete, isLoading }: DataTreeProps) => {
  const [selected, setSelected] = React.useState<number[]>([]);
  const [searchQuery, setSearchQuery] = React.useState<string>('');

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(getAllIds(rows));
    } else {
      setSelected([]);
    }
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: number) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else {
      newSelected = [...selected];
      newSelected.splice(selectedIndex, 1);
    }
    setSelected(newSelected);
  };

  const getAllIds = (rows: any[]): any[] => {
    const result = [];
    for (let i=0; i<rows.length; i++) {
      result.push(rows[i].id);
      if (rows[i].children) {
        const childIds = getAllIds(rows[i].children);
        result.splice(result.length, 0, ...childIds);
      }
    }
    return result;
  }

  const getAllRowCount = () => {
    return getAllIds(rows).length;
  }

  React.useEffect(() => {
    setSelected([]);
  }, [rows]);

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        {
          !hideToolBar && <DataTableToolbar title={title} selected={selected} onDelete={onBulkDelete} onSearch={setSearchQuery} />
        }
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size="medium"
          >
            <DataTableHeader
              numSelected={selected.length}
              onSelectAllClick={handleSelectAllClick}
              rowCount={getAllRowCount()}
              headCells={headCells}
              hideIdCheckbox={hideIdCheckbox}
            />
            <TableBody>
              {
                isLoading ? (
                  <TableRow
                    style={{
                      height: 200,
                    }}
                  >
                    <TableCell colSpan={headCells.length + 1} align='center' >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {rows.map((row: any) => {
                      const isItemSelected = selected.indexOf(row?.id) !== -1
                      return (
                        <CustomTreeTableRow 
                          isItemSelected={isItemSelected} 
                          selected={selected} 
                          row={row} 
                          searchQuery={searchQuery}
                          handleClick={handleClick} 
                          key={row?.id} 
                          headCells={headCells} 
                          hideIdCheckbox={hideIdCheckbox} 
                        />
                      )
                    })}
                    {rows.length > 0 && (
                      <TableRow
                        style={{
                          height: 53 * rows.length,
                        }}
                      >
                        <TableCell colSpan={headCells.length + 1} />
                      </TableRow>
                    )}
                  </>
                )
              }
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
